import { Ref } from 'react';

import {
	FASTSHIFT_TICKET_STATUS,
	FastshiftTicketStatus,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import Lozenge from '@atlaskit/lozenge';

import { getTicketStatusColor, getTicketStatusText } from '@/common/utils/ticket-status';

import { FilterOption } from '../../types';
import { messages } from '../messages';

import { StatusFilterDropdownItem } from './status-filter-dropdown-item/status-filter-dropdown-item';

export const StatusFilterDropdown = ({
	selectedStatus,
	onStatusChange,
	sendStatusFilterDropdownClickUIEvent,
	sendStatusFilterDropdownItemClickUIEvent,
}: {
	selectedStatus: FilterOption<FastshiftTicketStatus> | null;
	onStatusChange: (option: FilterOption<FastshiftTicketStatus> | null) => void;
	sendStatusFilterDropdownClickUIEvent: () => void;
	sendStatusFilterDropdownItemClickUIEvent: (selectedStatus: string) => void;
}) => {
	const { formatMessage } = useIntl();

	const statusOptions: FilterOption<FastshiftTicketStatus>[] = Object.values(
		FASTSHIFT_TICKET_STATUS,
	).map((status) => {
		const statusMessage = getTicketStatusText(status);
		const statusText = formatMessage(statusMessage);
		return {
			label: statusText,
			value: status,
		};
	});

	return (
		<DropdownMenu
			// This is atlaskit's recommended pattern for custom trigger components
			// eslint-disable-next-line react/no-unstable-nested-components
			trigger={({ triggerRef, ...triggerProps }) => (
				<Button
					ref={triggerRef as Ref<HTMLButtonElement>}
					{...triggerProps}
					testId="status-filter-button"
					onClick={(e) => {
						sendStatusFilterDropdownClickUIEvent();
						triggerProps.onClick?.(e);
					}}
					isSelected={!!selectedStatus}
					iconAfter={ChevronDownIcon}
				>
					{selectedStatus?.label ? (
						<Lozenge appearance={getTicketStatusColor(selectedStatus.value)}>
							{selectedStatus.label}
						</Lozenge>
					) : (
						<FormattedMessage {...messages.migrationTicketsFiltersStatusPlaceholder} />
					)}
				</Button>
			)}
			spacing="compact"
			shouldRenderToParent
		>
			<DropdownItemGroup>
				{statusOptions.map((option) => (
					<StatusFilterDropdownItem
						key={option.value}
						option={option}
						isSelected={selectedStatus?.value === option.value}
						onStatusChange={onStatusChange}
						sendStatusFilterDropdownItemClickUIEvent={sendStatusFilterDropdownItemClickUIEvent}
					/>
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
